import { Dayjs } from 'dayjs';

import { SBaseDisplay, SDate } from '@summit/core';
import { DayOfWeekAbbrMapping, NumberFormatter, SummitFormattedText } from '@summit/models';

import { EboObject } from '../base-classes/ebo-object';
import { EboObjectTimePeriod } from '../base-classes/ebo-object-time-period';
import { Employee } from '../employee/employee';
import { Company } from '../organization/company/company';
import { PayUnit } from '../payroll/pay-unit.enum';
import { Hub } from '../places/hub/hub';
import { TruckAlias } from '../truck/truck-alias';
import { RouteAltWeek } from './route-alt-week';
import { RouteHub } from './route-hub';
import { RouteLegWage } from './route-leg-wage';
import { RouteLinehaulAssignment } from './route-linehaul-assignment';
import { RouteRun } from './route-run';
import { RouteShift } from './route-shift.enum';
import { RouteStatus, RouteStatusMapping } from './route-status.enum';
import { RouteTimeline } from './route-timeline';
import { RouteType } from './route-type.enum';
import { RouteWage } from './route-wage';
import { ScheduleTemplateLeg } from './schedule-template-leg';

SummitFormattedText;

export class Route extends EboObject implements SBaseDisplay {
  public static UnknownRouteName = 'Unknown Route';
  public static HubTransferName = 'Hub Transfer';

  name: string;
  hubID: number;
  hub: Hub;
  supportingHubs: RouteHub[];
  displayName: string;
  shift: RouteShift;
  returnsHomeDaily: boolean;
  returnsHomeWeekly: boolean;
  settlementCompanyID: string;
  settlementCompany: Company;
  currentTimeline: RouteTimeline;
  timelines: RouteTimeline[];
  assignedLinehaulRuns: RouteLinehaulAssignment[];
  wages: RouteWage[];
  legWages: RouteLegWage[];
  //Schedule Config
  liveDispatched: boolean;
  excludeFromDispatch: boolean;
  excludeFromScheduleConflicts: boolean;
  scheduleSortOrder: number;
  //Schedule Details
  scheduleAltWeeks: RouteAltWeek[];
  scheduleTemplateComment: string;
  scheduleTemplates: ScheduleTemplateLeg[]; //Client Only
  runs: RouteRun[];
  defaultDriver1ID: string;
  defaultDriver1: Employee;
  defaultDriver2ID: string;
  defaultDriver2: Employee;
  defaultTruckAliasID: string;
  defaultTruckAlias: TruckAlias;
  usedForLinehaul: boolean;

  directions?: string;
  //Used for Schedule
  currentAvailability: number;
  currentAltWeekId: string;
  groupIds: string[];
  //new Route
  newType: RouteType;
  newStatus: RouteStatus;
  newEffectiveDate: Dayjs;

  get exclusivelyLegWage(): boolean {
    return this.currentTimeline?.exclusivelyLegWage;
  }
  get currentWages(): RouteWage[] {
    const cutOff = SDate.getNormalized();
    return this.wages.filter(
      (entry) =>
        !entry.employeeId &&
        cutOff.isSameOrAfter(entry.startTime) &&
        (!entry.endTime || cutOff.isSameOrBefore(entry.endTime))
    );
  }

  constructor(jsonData?: Partial<Route>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.hub) this.hub = new Hub(jsonData.hub);
      if (jsonData.supportingHubs) this.supportingHubs = jsonData.supportingHubs.map((data) => new RouteHub(data));
      if (jsonData.defaultDriver1) this.defaultDriver1 = new Employee(jsonData.defaultDriver1);
      if (jsonData.defaultDriver2) this.defaultDriver2 = new Employee(jsonData.defaultDriver2);
      if (jsonData.defaultTruckAlias) this.defaultTruckAlias = new TruckAlias(jsonData.defaultTruckAlias);
      if (jsonData.settlementCompany) {
        this.settlementCompany = new Company(jsonData.settlementCompany);
      }
      if (jsonData.timelines) {
        this.timelines = jsonData.timelines.map((data) => new RouteTimeline(data));
      }
      if (jsonData.currentTimeline) {
        this.currentTimeline = new RouteTimeline(jsonData.currentTimeline);
      }
      if (jsonData.assignedLinehaulRuns) {
        this.assignedLinehaulRuns = jsonData.assignedLinehaulRuns.map((data) => new RouteLinehaulAssignment(data));
      }
      if (jsonData.runs) {
        this.runs = jsonData.runs.map((item) => new RouteRun(item));
      }
      if (jsonData.scheduleAltWeeks) {
        this.scheduleAltWeeks = jsonData.scheduleAltWeeks
          .map((data) => new RouteAltWeek(data))
          .sort((a, b) => {
            if (a.order > b.order) return 1;
            if (a.order < b.order) return -1;
            return a.name > b.name ? 1 : -1;
          });
        this.scheduleTemplates = this.scheduleAltWeeks
          .map((m) => m.templates)
          .reduce((acc, value) => acc.concat(value), []);
      }
      if (jsonData.wages) {
        this.wages = jsonData.wages
          .map((item) => new RouteWage(item))
          .sort((a, b) => {
            if (!a.endTime && b.endTime) return 1;
            if (a.endTime && !b.endTime) return -1;
            if (!a.employeeId && b.employeeId) return 1;
            if (a.employeeId && !b.employeeId) return -1;
            if (!a.endTime && !b.endTime) return b.ruleOrder - a.ruleOrder;
            if (a.startTime.isAfter(b.startTime)) return 1;
            if (a.startTime.isBefore(b.startTime)) return -1;
            return b.ruleOrder - a.ruleOrder;
          });
      }
      if (jsonData.legWages) {
        this.legWages = jsonData.legWages.map((item) => new RouteLegWage(item)).sort(RouteLegWage.sort);
      }
      if (jsonData.groupIds) this.groupIds = jsonData.groupIds;
    } else {
      this.newEffectiveDate = SDate.getNormalized();
      this.newType = RouteType.SoloUnassigned;
      this.newStatus = RouteStatus.Active;
    }
    this.shift ??= RouteShift.Day;
    this.runs ??= [];
    this.usedForLinehaul ??= true;
    this.assignedLinehaulRuns ??= [];
    this.timelines ??= [
      new RouteTimeline({
        routeID: this.id,
        status: RouteStatus.Active,
        type: RouteType.SoloUnassigned,
        effectiveDate: SDate.getNormalized()
      })
    ];
    this.supportingHubs ??= [];
    this.scheduleAltWeeks ??= [];
    this.scheduleTemplates ??= [];
    this.groupIds ??= [];
    this.wages ??= [];
    this.legWages ??= [];
    this.excludeFromDispatch ??= false;
    this.returnsHomeDaily ??= false;
    this.returnsHomeWeekly ??= false;
    this.excludeFromScheduleConflicts ??= false;
    this.liveDispatched ??= false;
  }

  public get defaultSort(): string {
    return `${NumberFormatter(this.hubID, 5)}-${this.name}`;
  }

  public get hubName(): string {
    return this.hub ? this.hub.toShortString() : 'Unknown';
  }
  public get expectedMiles(): number {
    return this.currentTimeline?.expectedMiles ? this.currentTimeline.expectedMiles : null;
  }

  public get status(): RouteStatus {
    return this.currentTimeline?.status ?? RouteStatus.Unknown;
  }
  public get type(): RouteType {
    return this.currentTimeline?.type ?? RouteType.Unknown;
  }
  public get typeSummary(): SummitFormattedText {
    return this.currentTimeline?.typeSummary;
  }

  public get perDiemDays(): number | null {
    return this.currentTimeline?.perDiemDays;
  }

  public get payDescription(): SummitFormattedText {
    const response = new SummitFormattedText();
    this.wages
      .filter((entry) => entry.units == PayUnit.Dollars || entry.units == PayUnit.Miles)
      .filter(EboObjectTimePeriod.filterToEffectiveNow)
      .forEach((entry) => {
        response.AddKeyValues(entry.applicableEmployee, entry.toShortString('routePayView'));
      });
    console.log(`${this.name} legWages`, this.legWages);
    this.legWages
      .filter(EboObjectTimePeriod.filterToEffectiveNow)
      .sort(RouteLegWage.sort)
      //.filter((entry) => entry.comment || entry.ruleSetId)
      .filter(
        (value, index, self) =>
          value.ruleSetId?.length > 0 ||
          self.findIndex(
            (arrayValue: RouteLegWage) =>
              arrayValue.employeeId == value.employeeId &&
              arrayValue.payrollGroupId == value.payrollGroupId &&
              arrayValue.comment == value.comment
          ) === index
      )
      .forEach((entry) => {
        if (entry.ruleSetId) response.AddText(entry.applicableEmployee);
        else
          response.AddKeyValues(
            entry.applicableEmployee.toString(),
            entry.comment?.length > 0 ? entry.comment : entry.toShortString()
          );
      });
    this.wages
      .filter((entry) => entry.units == PayUnit.MileageRate)
      .filter(EboObjectTimePeriod.filterToEffectiveNow)
      .forEach((entry) => {
        response.AddKeyValues(entry.applicableEmployee, entry.toShortString('routePayView'));
      });
    return response;
  }

  public get currentLinehaulRuns(): RouteLinehaulAssignment[] | null {
    return this.assignedLinehaulRuns.filter((entry) => entry.endTime == null || entry.endTime > SDate.new());
  }

  public get departures(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.runs.length > 1) {
      this.scheduleTemplates
        .filter((entry) => entry.legCount == 1)
        .forEach((entry) => {
          response.AddKeyValues(
            `${DayOfWeekAbbrMapping[entry.departureDayOfWeek]} - ${entry.run.name}`,
            `${entry.departureDateTimeString}`
          );
        });
    } else {
      this.scheduleTemplates
        .filter((entry) => entry.legCount == 1)
        .forEach((entry) => {
          response.AddKeyValues(
            `${DayOfWeekAbbrMapping[entry.departureDayOfWeek]}`,
            `${entry.departureDateTimeString}`
          );
        });
    }
    return response.isEmpty() ? null : response;
  }
  public get linehaulDetails(): SummitFormattedText {
    const response = new SummitFormattedText();
    this.currentLinehaulRuns.forEach((entry) => {
      entry.linehaulRun.details.forEach((detail) => {
        response.AddLine(detail.toShortString());
      });
    });
    return response;
  }

  public isActive(): boolean {
    return this.currentTimeline && this.currentTimeline.status === RouteStatus.Active;
  }

  public isNotActive(): boolean {
    return !this.isActive();
  }

  public isInActive(): boolean {
    return this.currentTimeline && this.currentTimeline.status === RouteStatus.Inactive;
  }

  public isDeprecated(): boolean {
    return this.isNotActive();
  }

  public toShortString(): string {
    let response = this.name;
    if (this.hubID) {
      response = `${this.hubID} - ${response}`;
    }
    if (this.currentTimeline && this.isNotActive()) {
      response += ` (${
        this.currentTimeline ? RouteStatusMapping[this.currentTimeline.status] : RouteStatusMapping[RouteStatus.Unknown]
      })`;
    }
    return response;
  }

  public getCurrentTimeline(): RouteTimeline {
    const response = this.timelines
      .filter((entry) => entry.effectiveDate.isSameOrBefore(SDate.newUtc()))
      .sort((n1, n2) => n1.effectiveDate.diff(n2.effectiveDate));
    // console.log('timelines', response);

    return response.length > 0 ? response[0] : null;
  }

  public getFutureTimelines(): RouteTimeline[] {
    const response = this.timelines
      .filter((entry) => entry.effectiveDate.isAfter(SDate.newUtc()))
      .sort((n1, n2) => n1.effectiveDate.diff(n2.effectiveDate));
    // console.log('timelines', response);

    return response;
  }

  getNextWageRefNumber(): number {
    let maxRuleNum = 0;
    if (this.wages && this.wages.length > 0) {
      maxRuleNum = this.wages
        .map((s) => s.ruleOrder)
        .reduce(function (a, b) {
          return Math.max(a, b);
        });
    }
    return (maxRuleNum ?? 0) + 1;
  }
  getNextLegWageRefNumber(): number {
    let maxRuleNum = 0;
    if (this.legWages && this.legWages.length > 0) {
      maxRuleNum = this.legWages
        .map((s) => s.ruleOrder)
        .reduce(function (a, b) {
          return Math.max(a, b);
        });
    }
    return (maxRuleNum ?? 0) + 1;
  }
}
